import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    /*
     * Open the Quick Add immediately on the first click.
     * The product content is then loaded into the already-open dialog.
     * This avoids the first-click delay caused by fetching the product page
     * before opening the dialog, without changing the mobile layout.
     */
    this.#openQuickAddModal();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    try {
      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement =
            html.querySelector('[data-product-grid-content]') ||
            html.querySelector('.product-information__grid');
          if (gridElement) {
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }
    } catch (error) {
      console.error('Quick Add failed to load product content:', error);
    }
  };
  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // MATEEVS speed optimisation: the first product image is visible
      // immediately when Quick Add opens. Make only that image eager/high
      // priority; leave all other images untouched.
      const firstQuickAddImage = html.querySelector('.product-information__media img, img');
      if (firstQuickAddImage instanceof HTMLImageElement) {
        firstQuickAddImage.loading = 'eager';
        firstQuickAddImage.fetchPriority = 'high';
      }

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      const productHeaderTop = document.createElement('div');
      productHeaderTop.classList.add('mateevs-quick-add__header-top');

      productTitle.classList.add('mateevs-quick-add__title');
      productHeaderTop.appendChild(productTitle);

      const reviewRating = Number.parseFloat(this.dataset.reviewRating);
      const reviewCount = Number.parseInt(this.dataset.reviewCount, 10);
      if (Number.isFinite(reviewRating) && reviewRating > 0) {
        const review = document.createElement('div');
        review.classList.add('mateevs-quick-add__review');
        review.setAttribute('aria-label', `${reviewRating.toFixed(1)} out of 5 stars${Number.isFinite(reviewCount) && reviewCount > 0 ? ` from ${reviewCount} reviews` : ''}`);

        const stars = document.createElement('span');
        stars.classList.add('mateevs-quick-add__stars');
        stars.setAttribute('aria-hidden', 'true');
        stars.textContent = '★★★★★';

        const score = document.createElement('span');
        score.classList.add('mateevs-quick-add__score');
        score.textContent = reviewRating.toFixed(1);

        if (Number.isFinite(reviewCount) && reviewCount > 0) {
          const count = document.createElement('span');
          count.classList.add('mateevs-quick-add__count');
          count.textContent = `(${reviewCount.toLocaleString()})`;
          review.append(stars, score, count);
        } else {
          review.append(stars, score);
        }

        productHeaderTop.appendChild(review);
      }

      productHeader.appendChild(productHeaderTop);

      const inspiredBy = (this.dataset.inspiredBy || '').trim();
      if (inspiredBy) {
        const inspiredTag = document.createElement('div');
        inspiredTag.classList.add('mateevs-quick-add__inspired');
        inspiredTag.innerHTML = '<span>Inspired by</span><strong></strong>';
        inspiredTag.querySelector('strong').textContent = inspiredBy;
        productHeader.appendChild(inspiredTag);
      }

      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      const fullDetailsWrapper = document.createElement('div');
      fullDetailsWrapper.classList.add('mateevs-quick-add__full-details');
      const fullDetailsLink = document.createElement('a');
      fullDetailsLink.href = this.productPageUrl;
      fullDetailsLink.textContent = 'View full details';
      fullDetailsLink.setAttribute('aria-label', 'View full details for ' + (this.dataset.productTitle || 'this product'));
      fullDetailsWrapper.appendChild(fullDetailsLink);
      productGrid.appendChild(fullDetailsWrapper);

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    } else {
      const productMedia = productGrid.querySelector('.product-information__media');
      const productDetails = productGrid.querySelector('.product-details');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const productPrice = productGrid.querySelector('product-price');

      /*
       * Desktop uses a dedicated drawer DOM instead of morphing the full
       * product-page grid. The full product grid contains PDP-only layout
       * rules that can hide its children inside Quick Add, which was leaving
       * the drawer visually empty.
       */
      if (productMedia && productDetails) {
        const desktopRoot = document.createElement('div');
        desktopRoot.classList.add('mateevs-quick-add--desktop');

        const productHeader = document.createElement('div');
        productHeader.classList.add('mateevs-quick-add__desktop-header');

        const productHeaderTop = document.createElement('div');
        productHeaderTop.classList.add('mateevs-quick-add__desktop-header-top');

        const productTitle = document.createElement('a');
        productTitle.classList.add('mateevs-quick-add__desktop-title');
        productTitle.href = this.productPageUrl;
        productTitle.textContent = this.dataset.productTitle || '';
        productHeaderTop.appendChild(productTitle);

        const reviewRating = Number.parseFloat(this.dataset.reviewRating);
        const reviewCount = Number.parseInt(this.dataset.reviewCount, 10);
        if (Number.isFinite(reviewRating) && reviewRating > 0) {
          const review = document.createElement('div');
          review.classList.add('mateevs-quick-add__desktop-review');
          review.setAttribute(
            'aria-label',
            `${reviewRating.toFixed(1)} out of 5 stars${Number.isFinite(reviewCount) && reviewCount > 0 ? ` from ${reviewCount} reviews` : ''}`
          );

          const stars = document.createElement('span');
          stars.classList.add('mateevs-quick-add__desktop-stars');
          stars.setAttribute('aria-hidden', 'true');
          stars.textContent = '★★★★★';

          const score = document.createElement('span');
          score.classList.add('mateevs-quick-add__desktop-score');
          score.textContent = reviewRating.toFixed(1);
          review.append(stars, score);

          if (Number.isFinite(reviewCount) && reviewCount > 0) {
            const count = document.createElement('span');
            count.classList.add('mateevs-quick-add__desktop-count');
            count.textContent = `(${reviewCount.toLocaleString()})`;
            review.appendChild(count);
          }

          productHeaderTop.appendChild(review);
        }

        productHeader.appendChild(productHeaderTop);

        const inspiredBy = (this.dataset.inspiredBy || '').trim();
        if (inspiredBy) {
          const inspiredTag = document.createElement('div');
          inspiredTag.classList.add('mateevs-quick-add__desktop-inspired');
          inspiredTag.innerHTML = '<span>Inspired by</span><strong></strong>';
          inspiredTag.querySelector('strong').textContent = inspiredBy;
          productHeader.appendChild(inspiredTag);
        }

        desktopRoot.appendChild(productHeader);
        desktopRoot.appendChild(productMedia);

        const desktopDetails = document.createElement('div');
        desktopDetails.classList.add('mateevs-quick-add__desktop-details');

        if (productPrice && variantPicker) {
          const optionHead = document.createElement('div');
          optionHead.classList.add('mateevs-quick-add__desktop-option-head');

          const optionLabel = document.createElement('span');
          optionLabel.textContent = 'Size';

          optionHead.append(optionLabel, productPrice);
          desktopDetails.appendChild(optionHead);
        }

        if (variantPicker) {
          desktopDetails.appendChild(variantPicker);
        }

        const fullDetailsWrapper = document.createElement('div');
        fullDetailsWrapper.classList.add('mateevs-quick-add__desktop-full-details');

        const fullDetailsLink = document.createElement('a');
        fullDetailsLink.href = this.productPageUrl;
        fullDetailsLink.textContent = 'View full details';
        fullDetailsLink.setAttribute(
          'aria-label',
          'View full details for ' + (this.dataset.productTitle || 'this product')
        );

        fullDetailsWrapper.appendChild(fullDetailsLink);
        desktopDetails.appendChild(fullDetailsWrapper);

        if (productFormComponent) {
          desktopDetails.appendChild(productFormComponent);
        }

        desktopRoot.appendChild(desktopDetails);
        modalContent.replaceChildren(desktopRoot);
        this.#syncVariantSelection(modalContent);
        return;
      }
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/*
 * MATEEVS DESKTOP QUICK ADD DIRECT BUTTON BRIDGE
 *
 * Some product cards render the direct add button as the visible + icon.
 * On desktop only, intercept that button and route it to the Quick Add
 * drawer instead. Mobile is deliberately left completely untouched.
 */
if (!window.__mateevsDesktopQuickAddBridge) {
  window.__mateevsDesktopQuickAddBridge = true;

  document.addEventListener(
    'click',
    (event) => {
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('.quick-add .quick-add__button--add');
      if (!button) return;

      const component = button.closest('quick-add-component');
      if (!(component instanceof QuickAddComponent)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      component.handleClick(event);
    },
    true
  );
}
